/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import * as Sentry from '@sentry/react';

import API from 'js/lib/api';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, InlineNotification, Typography2, breakpoints } from '@coursera/cds-core';

import Alert from 'bundles/authentication-common/components/Alert';
import AuthenticationV3CtaTracking from 'bundles/authentication-common/components/AuthenticationV3CtaTracking';
import LinkButton from 'bundles/authentication-common/components/LinkButton';
import Separator from 'bundles/authentication-common/components/Separator';
import SocialSSO from 'bundles/authentication-common/components/authenticationModal/SocialSSO';
import RecaptchaDisclaimer from 'bundles/authentication-common/components/botManager/RecaptchaDisclaimer';
import LoginForm from 'bundles/authentication-common/components/forms/LoginForm';
import type SSOError from 'bundles/authentication-common/components/sso/SSOError';
import { InvalidDomainSSOError } from 'bundles/authentication-common/components/sso/SSOError';
import {
  AUTHENTICATION_FORM_VARIANTS,
  type AUTHENTICATION_FORM_VARIANTS_KEY,
  AUTHENTICATION_MODAL_MODES,
  type AUTHENTICATION_MODAL_MODES_KEY,
  LEARNER_HELP_CENTER_LINK,
  type ThirdPartyAuthService,
  getThirdPartyAuthServiceNameMapping,
} from 'bundles/authentication-common/constants';
import type { EnterpriseSsoLoginInfo } from 'bundles/authentication-common/hooks/useEnterpriseSsoCheck';
import useTrackClickButton from 'bundles/authentication-common/hooks/useTrackClickButton';
import type { ThirdPartyOrganizationsData } from 'bundles/authentication-common/hooks/useUserThirdPartyOrganizationsQuery';
import type { ArkoseBotManagerContext } from 'bundles/authentication-common/types/BotManagerTypes';
import {
  SSO_VARIANT,
  completeLtiLaunchIfPending,
  getSSOVariant,
  isLtiInvalidDomainSSOError,
} from 'bundles/authentication-common/utils/ssoUtils';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import SSOButton from 'bundles/user-account/components/SsoButton';
import Instrumentation from 'bundles/userModal/lib/instrumentation';

import _t from 'i18n!nls/authentication';

type Props = {
  className?: string;
  defaultEmail?: string;
  navigate: (path: AUTHENTICATION_MODAL_MODES_KEY, options?: $TSFixMe) => void;
  onLogin: () => void;
  onSSOError: (error: SSOError) => void;
  onSSOSuccess: (isRegistration: boolean) => void;
  preventRegister?: boolean;
  redirect?: string;
  magicLinkRedirect?: string;
  arkoseBotManagerContext?: ArkoseBotManagerContext;
  isEnterprise?: boolean;
  shouldShowCustomSSO?: boolean;
  forceSSOLogin?: boolean;
  authProvider?: string;
  hideOrganizationSSO?: boolean;
  formVariant?: AUTHENTICATION_FORM_VARIANTS_KEY;
  showProgressiveAuth?: boolean;
  thirdPartyAuthService?: ThirdPartyAuthService;
  areAnyApiRequestsInFlight?: boolean;
  ssoError?: SSOError;
  resetSsoError?: () => void;
  enterpriseSsoEmailCheckInfo?: {
    enterpriseSsoLoginInfo: EnterpriseSsoLoginInfo;
    onEmailChange: (email: string) => void;
    onContinueWithSso: () => void;
    resetEnterpriseSsoInfo: () => void;
  };
} & ThirdPartyOrganizationsData;

const TrackedLinkButton = withSingleTracked({ type: 'BUTTON' })(LinkButton);

const api = API('', { type: 'rest' });

const styles = {
  container: css`
    display: grid;
    grid-gap: var(--cds-spacing-300);
    grid-template-columns: 1fr;
    margin: 0 auto;
    max-width: 396px;
    padding: var(--cds-spacing-50);

    .cds-text-field-root {
      margin-top: var(--cds-spacing-50) !important;
    }

    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-300) var(--cds-spacing-200);
    }
  `,
  navigation: css`
    margin: 16px 0 0;
  `,
  link: css`
    text-decoration: underline;
  `,
  organization: css`
    margin: 0;
  `,
  section: css`
    &:nth-child(1) {
      -ms-grid-row: 1;
    }

    &:nth-child(2) {
      -ms-grid-row: 2;
    }

    &:nth-child(3) {
      -ms-grid-row: 3;
    }
  `,
  separator: css`
    margin: var(--cds-spacing-150) 0 !important;
  `,
  terms: css`
    margin: var(--cds-spacing-100) 0 0;
    padding: 0;

    &:first-of-type {
      margin: 0;
    }

    &:nth-of-type(n) a {
      color: inherit;
      text-decoration: underline;
    }
  `,
  ctaButtonSpacing: css`
    margin-top: var(--cds-spacing-200);
  `,
  notification: css`
    margin-bottom: 28px;
  `,
};

const Login = ({
  navigate,
  onLogin,
  onSSOSuccess,
  className,
  defaultEmail,
  onSSOError,
  preventRegister,
  arkoseBotManagerContext,
  isEnterprise,
  shouldShowCustomSSO,
  forceSSOLogin,
  organizations,
  authProvider,
  hideOrganizationSSO,
  areAnyApiRequestsInFlight,
  ssoError,
  resetSsoError,
  enterpriseSsoEmailCheckInfo,
  formVariant,
  showProgressiveAuth,
  thirdPartyAuthService,
  magicLinkRedirect,
}: Props) => {
  const { trackButton } = useTrackClickButton();

  const [error, setError] = useState<{ type: 'magicLink' | 'login' } | null>(null);
  const [currentEmail, setCurrentEmail] = useState(defaultEmail || '');
  // On invalid-domain errors, hide this button when a SAML magic link is already emailed
  // (`showResendLink` — the banner owns the resend CTA) or when the error originated from an
  // LTI/C4C launch (those orgs only support redirect-to-login on SSO failure, with no magic-link
  // path). Other invalid-domain screens keep the link fallback their copy points learners to.
  const hideLoginWithLink =
    (ssoError instanceof InvalidDomainSSOError && !!ssoError.showResendLink) || isLtiInvalidDomainSSOError(ssoError);

  const navigateToForgotPassword = () => {
    navigate(AUTHENTICATION_MODAL_MODES.FORGOT);
  };

  const navigateToRequestMagicLink = () => {
    navigate(AUTHENTICATION_MODAL_MODES.REQUESTMAGICLINK);
  };

  const navigateToOrganizationSSO = () => {
    navigate(AUTHENTICATION_MODAL_MODES.ORGANIZATIONLOGIN, { referrer: AUTHENTICATION_MODAL_MODES.LOGIN });
  };

  const navigateToSignup = () => {
    navigate(AUTHENTICATION_MODAL_MODES.SIGNUP);
  };

  const onMagicLinkError = () => {
    setError({ type: 'magicLink' });
  };

  const sendMagicLink = async () => {
    if (!currentEmail) return navigateToForgotPassword();
    try {
      await api.post('/api/magicLinks.v1/encode', { data: { email: currentEmail, redirectUrl: magicLinkRedirect } });
      return navigate(AUTHENTICATION_MODAL_MODES.MAGICLINK, { email: currentEmail });
    } catch (errorObj) {
      Sentry.captureException(errorObj, {
        extra: { message: 'Error sending magic link.' },
      });
      onMagicLinkError();
      return false;
    }
  };

  const errorMessageFactory = (type: 'magicLink' | 'login') => {
    if (type === 'magicLink') {
      return (
        <FormattedMessage
          message={_t('Ensure the email address you entered is valid and try again or contact our {helpCenterLink}.')}
          helpCenterLink={<a href={LEARNER_HELP_CENTER_LINK}>{_t('Learner Help Center')}</a>}
        />
      );
    }
    return (
      <FormattedMessage
        message={_t('An unexpected error has occurred. Please try again or contact our {helpCenterLink}.')}
        helpCenterLink={<a href={LEARNER_HELP_CENTER_LINK}>{_t('Learner Help Center')}</a>}
      />
    );
  };

  const onLoginError = () => {
    setError({ type: 'login' });
  };

  const handleEmailChange = (email: string) => {
    // Handle general email tracking for magic link
    setCurrentEmail(email);
  };

  const handleSuccess = (redirectURL: string) => {
    Instrumentation.login();

    onLogin();
  };

  const handleSSOSuccess = async (isRegistration: boolean) => {
    await completeLtiLaunchIfPending(ssoError);
    onSSOSuccess(isRegistration);
  };

  const isSocialSSOForm = formVariant === AUTHENTICATION_FORM_VARIANTS.SOCIALSSO && thirdPartyAuthService;

  const renderLoginNotificationMessage = () => {
    if (error) {
      return (
        <Alert css={styles.notification} type="error">
          {errorMessageFactory(error.type)}
        </Alert>
      );
    } else if (isSocialSSOForm) {
      return (
        <InlineNotification severity="information" css={styles.notification}>
          <Typography2 component="p" variant="bodyPrimary">
            {_t('This email previously logged in using #{thirdPartyAuthServiceName}', {
              thirdPartyAuthServiceName: getThirdPartyAuthServiceNameMapping()[thirdPartyAuthService],
            })}
          </Typography2>
        </InlineNotification>
      );
    } else {
      return null;
    }
  };

  const hideForm = organizations?.[0]?.supportsDegreeSSOLogin || forceSSOLogin;
  const shouldHideGenericSocialSSO = formVariant === AUTHENTICATION_FORM_VARIANTS.PASSWORD || isSocialSSOForm;
  const isEnterpriseSsoLogin = enterpriseSsoEmailCheckInfo?.enterpriseSsoLoginInfo?.isEnterpriseSsoLogin;
  const ssoVariant = getSSOVariant({ authProvider, isEnterprise });

  return (
    <section css={styles.container} className={className} data-catchpoint="authentication-box-content">
      <header>
        <Typography2 component="h2" variant="titleXSmall" align="left">
          {_t('Welcome back')}
        </Typography2>
      </header>
      <div css={styles.section}>
        {renderLoginNotificationMessage()}
        {/* TODO: Consolidate the whitelist organization for SSO login logic to `getSSOVariant` utility */}
        {authProvider && (areAnyApiRequestsInFlight || shouldShowCustomSSO) && (
          <>
            <SSOButton provider={authProvider} organizations={organizations} loading={areAnyApiRequestsInFlight} />
            {!areAnyApiRequestsInFlight && !forceSSOLogin && <Separator css={styles.separator} text={_t('or')} />}
          </>
        )}
        {!areAnyApiRequestsInFlight && !hideForm && (
          <LoginForm
            defaultEmail={defaultEmail}
            navigate={navigate}
            onError={onLoginError}
            onForgotPassword={navigateToForgotPassword}
            onRequestMagicLink={navigateToRequestMagicLink}
            onSuccess={handleSuccess}
            onEmailChange={handleEmailChange}
            arkoseBotManagerContext={arkoseBotManagerContext}
            onSSOError={onSSOError}
            enterpriseSsoProps={{
              ssoError,
              resetSsoError,
              isEnterpriseSsoLogin,
              isLoading: enterpriseSsoEmailCheckInfo?.enterpriseSsoLoginInfo?.isLoading,
              isRedirecting: enterpriseSsoEmailCheckInfo?.enterpriseSsoLoginInfo?.isRedirecting,
              onEmailChange: enterpriseSsoEmailCheckInfo?.onEmailChange,
              onSubmit: enterpriseSsoEmailCheckInfo?.onContinueWithSso,
            }}
            showProgressiveAuth={showProgressiveAuth}
            formVariant={formVariant}
          />
        )}
        {isSocialSSOForm && (
          <div css={styles.ctaButtonSpacing}>
            <SocialSSO onError={onSSOError} onSuccess={handleSSOSuccess} services={[thirdPartyAuthService]} />
          </div>
        )}
        {showProgressiveAuth && !isEnterpriseSsoLogin && !hideLoginWithLink && (
          <Button
            size="medium"
            variant="secondary"
            fullWidth
            css={styles.ctaButtonSpacing}
            onClick={() => {
              trackButton({ name: 'login_with_link' });
              sendMagicLink();
            }}
          >
            {_t('Login with link')}
          </Button>
        )}
        {ssoVariant === SSO_VARIANT.SOCIAL && !shouldHideGenericSocialSSO && (
          <>
            <Separator css={styles.separator} text={_t('or')} customStyles={{ margin: '22px 0' }} />
            <SocialSSO
              onError={onSSOError}
              onSuccess={handleSSOSuccess}
              services={thirdPartyAuthService ? [thirdPartyAuthService] : undefined}
            />
          </>
        )}
        {!preventRegister && (
          <Typography2
            component="p"
            variant="bodySecondary"
            align="left"
            css={styles.navigation}
            className="auth-form-type-navigation"
          >
            <FormattedMessage
              message={_t('New to Coursera? {signupLink}')}
              signupLink={
                <AuthenticationV3CtaTracking>
                  {(trackV3) => {
                    const handleOnClick = () => {
                      trackV3({ name: 'signup' });
                      navigateToSignup();
                    };
                    return (
                      <TrackedLinkButton onClick={handleOnClick} trackingName="signup" css={styles.link}>
                        {_t('Sign up')}
                      </TrackedLinkButton>
                    );
                  }}
                </AuthenticationV3CtaTracking>
              }
            />
          </Typography2>
        )}
        {ssoVariant === SSO_VARIANT.SOCIAL && !hideOrganizationSSO && (
          <>
            <Separator css={styles.separator} customStyles={{ margin: '12px 0' }} />
            <Typography2 component="p" variant="bodySecondary" align="left" css={styles.organization}>
              <AuthenticationV3CtaTracking>
                {(trackV3) => {
                  const handleOnClick = () => {
                    trackV3({ name: 'signup_with_other_sso' });
                    navigateToOrganizationSSO();
                  };
                  return (
                    <TrackedLinkButton
                      onClick={handleOnClick}
                      trackingName="sign_up_with_organization_btn_click"
                      css={styles.link}
                    >
                      {_t('Log in with your organization')}
                    </TrackedLinkButton>
                  );
                }}
              </AuthenticationV3CtaTracking>
            </Typography2>
          </>
        )}
      </div>
      <div css={styles.section} className="auth-form-tos-section">
        <Typography2 component="p" variant="bodyTertiary" color="supportText" align="left" css={styles.terms}>
          <FormattedMessage
            message={_t('Having trouble logging in? {helpCenterLink}')}
            helpCenterLink={
              <a href={LEARNER_HELP_CENTER_LINK} target="_blank" rel="noopener noreferrer">
                {_t('Learner help center')}
              </a>
            }
          />
        </Typography2>
        {!areAnyApiRequestsInFlight && !forceSSOLogin && (
          <RecaptchaDisclaimer
            isEnterprise
            variant="bodyTertiary"
            color="supportText"
            align="left"
            css={styles.terms}
          />
        )}
      </div>
    </section>
  );
};

export default Login;
